import { useCallback, useEffect, useRef, useState } from "react";
import type * as React from "react";
import { Box, Divider, Typography, useMediaQuery } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import GiftCheckoutStepper from "components/v3/pages/account/GiftCheckoutStepper";
import GiftRecipientsCheckoutSidebar from "components/v3/pages/account/GiftRecipientsCheckoutSidebar";
import SideMenuLayout from "components/v3/pages/onboarding/SideMenuLayout";
import LoadingAnimation from "components/v3/sharedComponents/LoadingAnimation";
import { COLORS } from "components/v3/Theme/colors";
import { GIFT_UNIT_PRICE_USD } from "constants/membershipPriceConstants";
import { PATHS } from "constants/pathConstants";
import { usePaymentIframeEmbedGuard } from "hooks/usePaymentIframeEmbedGuard";
import { useUnaricPaypagePostMessage } from "hooks/useUnaricPaypagePostMessage";
import { trackPaymentCompleted, trackPaymentFailed } from "analytics";
import { openGLSnackbar } from "store/reducers/snackbar/openGLSnackbarSlice";
import PageTitle from "utils/PageTitle";

const LOADING_VIEWPORT_MAX_PX = 500;
const IFRAME_MIN_HEIGHT_PX = 850;
const IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX = 200;
const IFRAME_NARROW_MAX_WIDTH_CSS = "(max-width: 526px)";
const EXPAND_MAX_PX = 1600;
const IFRAME_ROUNDED_CORNER_RADIUS_PX = 10;
const IFRAME_ROUNDED_CORNER_PADDING_PX = 10;
const IFRAME_CHROME_VERTICAL_EXTRA_PX = IFRAME_ROUNDED_CORNER_PADDING_PX * 2;

type GiftPaymentLocationState = {
  ecommerceLink: string;
  giftQuantity?: number;
};

const GiftPaymentPage: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const routeState = location.state as GiftPaymentLocationState | undefined;
  const ecommerceLink = routeState?.ecommerceLink ?? null;

  const [iframeLoaded, setIframeLoaded] = useState(false);
  const paymentIframeRef = useRef<HTMLIFrameElement>(null);

  const isNarrowPaymentViewport = useMediaQuery(IFRAME_NARROW_MAX_WIDTH_CSS, {
    defaultMatches: false
  });
  const iframeMinHeightLoaded =
    IFRAME_MIN_HEIGHT_PX +
    (isNarrowPaymentViewport ? IFRAME_MIN_HEIGHT_NARROW_EXTRA_PX : 0);
  const outerMinHeightLoading =
    LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMinHeightLoaded =
    iframeMinHeightLoaded + IFRAME_CHROME_VERTICAL_EXTRA_PX;
  const outerMaxHeightLoaded = EXPAND_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX;

  const planCount = Math.max(1, routeState?.giftQuantity ?? 1);

  useEffect(() => {
    setIframeLoaded(false);
  }, [ecommerceLink]);

  const handlePaymentEmbedFailed = useCallback(() => {
    trackPaymentFailed({
      paymentType: "gift_membership",
      failureReason: "payment_form_embed_failed"
    });
    dispatch(
      openGLSnackbar({
        variant: "error",
        header: "",
        subText:
          "The payment form couldn’t be loaded. Please try again and reach out to support if the problem persists."
      })
    );
    history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS);
  }, [dispatch, history]);

  usePaymentIframeEmbedGuard({
    ecommerceLink,
    iframeLoaded,
    enabled: Boolean(ecommerceLink),
    onEmbedFailed: handlePaymentEmbedFailed
  });

  const giftCheckoutCompleteRef = useRef(false);

  const navigateToGiftReceipt = useCallback(() => {
    history.push(PATHS.ACCOUNT_MANAGEMENT_PAYMENT_RECEIPT, {
      giftMembershipPurchased: true,
      quantity: planCount,
      totalUsd: planCount * GIFT_UNIT_PRICE_USD
    });
  }, [history, planCount]);

  const handleGiftCheckoutSuccess = useCallback(() => {
    if (giftCheckoutCompleteRef.current) return;
    giftCheckoutCompleteRef.current = true;
    trackPaymentCompleted({ paymentType: "gift_membership" });
    navigateToGiftReceipt();
  }, [navigateToGiftReceipt]);

  useUnaricPaypagePostMessage({
    iframeSrc: ecommerceLink,
    enabled: Boolean(ecommerceLink),
    paymentIframeRef,
    handlers: {
      onSuccessScreen: handleGiftCheckoutSuccess,
      onComplete: handleGiftCheckoutSuccess,
      onCancel: () => {
        dispatch(
          openGLSnackbar({
            variant: "info",
            header: "",
            subText: "Payment was cancelled."
          })
        );
        history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS);
      },
      onErrorWithMessage: (message) => {
        trackPaymentFailed({
          paymentType: "gift_membership",
          failureReason: message ? "payment_processor_error" : "payment_error"
        });
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText:
              message ||
              "We couldn’t process the payment. You can try again or contact support."
          })
        );
      },
      onErrorScreen: () => {
        trackPaymentFailed({
          paymentType: "gift_membership",
          failureReason: "payment_error_screen"
        });
        dispatch(
          openGLSnackbar({
            variant: "error",
            header: "",
            subText: "There was a problem with the payment."
          })
        );
      },
      onErrorFlowClosed: () => {
        history.push(PATHS.ACCOUNT_MANAGEMENT_GIFT_MEMBERSHIP);
      },
      onDirectDebitDialog: () => {
        globalThis.scrollTo(0, 0);
      }
    }
  });

  useEffect(() => {
    const state = location.state as GiftPaymentLocationState | undefined;
    if (!state?.ecommerceLink) {
      history.replace(PATHS.ACCOUNT_MANAGEMENT_GIFT_RECIPIENTS);
    }
  }, [history, location]);

  useEffect(() => {
    globalThis.scrollTo(0, 0);
  }, []);

  PageTitle("Gift membership");

  const mainContent = ecommerceLink ? (
    <Box
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: { lg: "row", xs: "column" },
        gap: 4,
        paddingX: { lg: 0, xs: 2 },
        paddingBottom: 4,
        width: "100%"
      }}
    >
      <Box
        sx={{
          bgcolor: COLORS.WHITE,
          borderRadius: "12px",
          boxSizing: "border-box",
          display: "flex",
          flex: 1,
          flexDirection: "column",
          gap: 0,
          minWidth: 0,
          p: { lg: 4, xs: 3 }
        }}
      >
        <Typography
          component="h1"
          sx={{ typography: { xs: "h5_black", md: "h4_black" } }}
        >
          Payment details
        </Typography>
        <Divider
          sx={{
            borderColor: alpha(COLORS.DARK_GRAY, 0.25),
            mb: 3,
            mt: 2
          }}
        />

        <Box
          sx={{
            position: "relative",
            boxSizing: "border-box",
            maxHeight: iframeLoaded
              ? outerMaxHeightLoaded
              : LOADING_VIEWPORT_MAX_PX + IFRAME_CHROME_VERTICAL_EXTRA_PX,
            maxWidth: "100%",
            minHeight: iframeLoaded
              ? outerMinHeightLoaded
              : outerMinHeightLoading,
            minWidth: 0,
            mx: "auto",
            overflow: iframeLoaded ? "visible" : "hidden",
            width: "100%"
          }}
        >
          <Box
            sx={{
              backgroundColor: COLORS.WHITE,
              borderRadius: `${IFRAME_ROUNDED_CORNER_RADIUS_PX}px`,
              boxSizing: "border-box",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              p: `${IFRAME_ROUNDED_CORNER_PADDING_PX}px`,
              width: "100%"
            }}
          >
            <iframe
              ref={paymentIframeRef}
              key={ecommerceLink}
              title="Gift payment"
              src={ecommerceLink}
              onError={handlePaymentEmbedFailed}
              onLoad={() => setIframeLoaded(true)}
              style={{
                border: "none",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
                maxWidth: "100%",
                minHeight: iframeLoaded
                  ? iframeMinHeightLoaded
                  : LOADING_VIEWPORT_MAX_PX,
                minWidth: 0,
                opacity: iframeLoaded ? 1 : 0,
                transition:
                  "min-height 450ms cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease-in",
                width: "100%"
              }}
            />
          </Box>
          {!iframeLoaded && (
            <Box
              sx={{
                alignItems: "center",
                bgcolor: "transparent",
                display: "flex",
                inset: 0,
                justifyContent: "center",
                position: "absolute"
              }}
            >
              <LoadingAnimation />
            </Box>
          )}
        </Box>
      </Box>

      <GiftRecipientsCheckoutSidebar planCount={planCount} showPricingSummary />
    </Box>
  ) : (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: 360,
        py: 4,
        width: "100%"
      }}
    >
      <LoadingAnimation />
    </Box>
  );

  return (
    <SideMenuLayout
      onboardingStepper={
        <GiftCheckoutStepper activeStepIndex={1} giftCount={planCount} />
      }
      stepperPlacement="top"
    >
      {mainContent}
    </SideMenuLayout>
  );
};

export default GiftPaymentPage;
